import { useFocusEffect, useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { BaseText, Headline1 } from 'aimo-ui';
import {
  CardBackground,
  CutWrapper,
  LeftCut,
  RightCut,
} from 'components/common/CrickleCutCardBackground';
import { ChangeSpaceItem } from 'components/longTermParking/TitleCard';
import ParkingContext from 'context/ParkingContext';
import useReadPurchasedPrepaidTickets from 'hooks/useReadPurchasedPrepaidTickets';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  confirmCreateSalesOrderData,
} from 'states/common';
import { usePermitStore } from 'stores/permitStore';
import {
  ScreenContainer,
  StyledImage,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { ParkzonePmcUnion } from 'types/common';
import { Pmc, ReservedParkingSpace } from 'types/generatedSchemaTypes';
import { ParkNavProps, RootStackParamList } from 'types/navigation';

import { showToast } from 'components/common/CustomToast';
import BillingInterval from 'components/longTermParking/BillingInterval';
import ValidFromPicker from 'components/longTermParking/ValidFromPicker';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import useSelectedCompanyBenefit from 'hooks/useSelectedCompanyBenefit.tsx';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import useSelectedPaymentMethod from 'hooks/useSelectedPaymentMethod.tsx';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { currencyFormatter, getPriceForSalesOrder } from 'utils/commonUtils';
import { showFeatureFlag } from 'utils/featureFlagUtils';
import { determinePaymentType } from 'utils/paymentUtils';
import {
  checkPrepaidOrPermitTicketDates,
  durationText,
  isItemPmc,
  isItemPredefinedTicket,
} from 'utils/permitUtils';
import { testIdentifiers } from 'utils/testIdentifiers';

const PrepaidTicketBuyView: React.FC = () => {
  const { t } = useTranslation();
  const { parkingZoneResult: parkingZone, selectedParkzonePmcUnion } =
    useContext(ParkingContext);

  const { selectedParkingVehiclePlate } = useParkingStore((state) => state);

  const selectedVehicle = useSelectedParkingVehicle(
    false,
    undefined,
    selectedParkingVehiclePlate
  );
  const selectPaymentMethod = useSelectedPaymentMethod();
  const { corporateAccountTransactionFee } = useSelectedCompanyBenefit();
  const {
    flags: { prepaid_display_valid_from_picker },
  } = useContext(FeatureFlagsContext);
  const [hasBeenPressed, setHasBeenPressed] = useState(false);
  const [vehicleChanged, setVehicleChanged] = useState(false);
  const showValidFromPicker = showFeatureFlag(
    prepaid_display_valid_from_picker
  );
  const selectedSegment = useParkzoneStore((state) => state.selectedTariffId);
  const { purchasedPrepaidTickets } = useReadPurchasedPrepaidTickets({});
  const { navigate } = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { setOptions } = useNavigation<ParkNavProps>();
  const selectedPrepaidStartTime =
    usePermitStore((state) => state.selectedPrepaidStartDate) ?? new Date();
  const selectedPermitStartTime = usePermitStore(
    (state) => state.selectedPermitStartDate
  );
  const setSelectedPrepaidStartDate = usePermitStore(
    (state) => state.setSelectedPrepaidStartDate
  );
  const validParkingStartDate = checkPrepaidOrPermitTicketDates(
    purchasedPrepaidTickets,
    selectedParkzonePmcUnion,
    selectedVehicle,
    selectedPermitStartTime,
    selectedSegment
  );

  const prevVehicle = useRef(selectedVehicle);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize date on mount with the initial validParkingStartDate
  useEffect(() => {
    if (!isInitialized) {
      setSelectedPrepaidStartDate(validParkingStartDate);
      setIsInitialized(true);
    }
  }, [isInitialized, validParkingStartDate]);

  // Handle vehicle changes
  useEffect(() => {
    if (isInitialized && prevVehicle.current !== selectedVehicle) {
      setHasBeenPressed(false);
      setVehicleChanged(true);
      setSelectedPrepaidStartDate(validParkingStartDate);
      showToast('success.validDateChange', 'info');
    }
    prevVehicle.current = selectedVehicle;
  }, [isInitialized, selectedVehicle, validParkingStartDate]);

  const canBuyNewPrepaidTicket = (): boolean => {
    if (purchasedPrepaidTickets.length === 0) {
      return true;
    }

    const now = new Date();
    const timeDiff = validParkingStartDate.getTime() - now.getTime();
    const hoursRemaining = timeDiff / (1000 * 60 * 60);

    return hoursRemaining <= 24;
  };

  useEffect(() => {
    setOptions({ title: t('parking.prepaidTickets.buyPrepaidTitle') });
  }, [setOptions]);
  useFocusEffect(
    React.useCallback(() => {
      usePermitStore
        .getState()
        .setBlockCompanybenefitInEdit('PURCHASE_PREPAID');

      return () => {
        usePermitStore.getState().setBlockCompanybenefitInEdit('');
      };
    }, [])
  );

  const {
    name = '',
    countryCode: zoneCountry,
    commercialZoneView,
    spotMap,
  } = parkingZone?.data?.parkZone || {};
  const isReserved =
    isItemPmc(selectedParkzonePmcUnion) &&
    selectedParkzonePmcUnion.reservationType === 'RESERVED';

  const getPriceText = (item: ParkzonePmcUnion) => {
    if (isItemPmc(item)) {
      return getPriceForSalesOrder(item.prices ?? [], zoneCountry || 'FI')
        .totalPrice;
    }
    if (isItemPredefinedTicket(item)) {
      return `${currencyFormatter(item.price ?? 0, zoneCountry || 'FI')}/${
        durationText(item) ?? item.name
      }`;
    }
  };

  const getNameText = (item: ParkzonePmcUnion) => {
    if (isItemPmc(item) || isItemPredefinedTicket(item)) {
      return durationString ? `${durationString} ${item.name}` : item.name;
    }
  };
  const priceString = getPriceText(selectedParkzonePmcUnion);

  const durationString = durationText(selectedParkzonePmcUnion);

  const showServiceFee = () => {
    if (!isItemPredefinedTicket(selectedParkzonePmcUnion)) {
      return '';
    }
    let serviceFee =
      determinePaymentType(selectPaymentMethod) === 'CORPORATE_ACCOUNT'
        ? corporateAccountTransactionFee
        : selectedParkzonePmcUnion?.serviceFee;

    return serviceFee !== 0 && typeof serviceFee === 'number'
      ? `+ ${currencyFormatter(serviceFee, zoneCountry || 'FI')} ${t(
          'parking.receipt.serviceFee'
        )}`
      : '';
  };

  useEffect(() => {
    return () => {
      confirmCreateSalesOrderData(undefined);
    };
  }, []);

  const selectSpaces = () => {
    useParkzoneStore
      .getState()
      .setSelectedParkingSpaces(
        ((selectedParkzonePmcUnion as Pmc).parkingSpacesV2?.content ??
          []) as ReservedParkingSpace[]
      );
    navigate('parkingSpaceMapViewer', { spotMap: spotMap ?? '' });
  };

  const getMinDaysBeforeStart = (): number => {
    const now = new Date();

    if (validParkingStartDate.toDateString() === now.toDateString()) {
      return 0;
    }

    const diffInMs = validParkingStartDate.getTime() - now.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    const result = Math.max(diffInDays, 0);

    return result;
  };

  const testID = 'prepaid-buy-screen';

  return (
    <ScreenContainer>
      <CardBackground>
        <StyledWrapper mb={27}>
          <StyledImage
            center={true}
            width={114}
            height={98}
            source={require('assets/images/permit.png')}
            {...testIdentifiers(`${testID}-image`)}
          />
        </StyledWrapper>
        <StyledWrapper mb={20}>
          <Headline1 center>{getNameText(selectedParkzonePmcUnion)}</Headline1>
        </StyledWrapper>
        <StyledWrapper center>
          <BaseText center {...testIdentifiers(`${testID}-parkzone-name`)}>
            {commercialZoneView?.zoneCode || ''}
            {commercialZoneView?.name ? ` ${commercialZoneView?.name}` : name}
          </BaseText>
          <BaseText center>
            {t('parking.prepaidTickets.buyScreenInfo')}
          </BaseText>
          <BaseText center {...testIdentifiers(`${testID}-pricing`)}>
            {`${priceString || ''} ${showServiceFee()}`}
          </BaseText>
        </StyledWrapper>
        <CutWrapper>
          <LeftCut />
          <RightCut />
        </CutWrapper>
        <StyledWrapper center maxWidth={320} mb={0}>
          <ValidFromPicker
            disabled={!(canBuyNewPrepaidTicket() && showValidFromPicker)}
            validFrom={new Date(validParkingStartDate)}
            selectedDate={new Date(selectedPrepaidStartTime)}
            onConfirm={(newDate) => {
              setSelectedPrepaidStartDate(newDate);
            }}
            maxDaysBeforeStart={30}
            mode="datetime"
            minDaysBeforeStart={getMinDaysBeforeStart()}
            testID={`${testID}-valid-selection`}
            vehicleChanged={vehicleChanged}
            hasBeenPressed={hasBeenPressed}
            setHasBeenPressed={setHasBeenPressed}
          />
        </StyledWrapper>
        {isItemPmc(selectedParkzonePmcUnion) && (
          <StyledWrapper center maxWidth={320} mb={0}>
            <BillingInterval />
          </StyledWrapper>
        )}
        {isReserved && (
          <StyledWrapper center maxWidth={320} mb={0}>
            <ChangeSpaceItem onPress={selectSpaces} testID={testID} />
          </StyledWrapper>
        )}
      </CardBackground>
    </ScreenContainer>
  );
};

export default PrepaidTicketBuyView;
